// (c) chaehongee. Confidential and proprietary.

import { useState, useRef, useMemo, useEffect, useCallback } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './TrainingPage.module.css'
import { createTrainingJob, type TrainingGender } from '../../../services/loras'
import {
    findUndecodableImages,
    prepareTrainingData,
} from '../../../services/training'
import { ArrowLeft, Upload, X, Sparkles, Loader2, ImagePlus, Lock } from 'lucide-react'
import { useBackstageNav } from '../navigator/Router'
import PrimaryButton from '../../PrimaryButton/PrimaryButton'
import { useReadOnlyMode } from '../../../store/hooks/useReadOnlyMode'
import { getUserProfile } from '../../../services/user'
import type { UserResponse } from 'models-ts/user'

export interface TrainingPageProps {
    onSuccess?: (loraId: string, loraName: string) => void
}

export default function TrainingPage({ onSuccess }: TrainingPageProps = {}): ReactElement {
    const { t } = useTranslation('canvas')
    const readOnly = useReadOnlyMode()
    const nav = useBackstageNav()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [files, setFiles] = useState<File[]>([])
    const [loraName, setLoraName] = useState<string>('')
    const [gender, setGender] = useState<TrainingGender>('female')
    const [status, setStatus] = useState<string>('')
    const [error, setError] = useState<string>('')
    const [isDragOver, setIsDragOver] = useState<boolean>(false)
    const [userProfile, setUserProfile] = useState<UserResponse | null>(null)
    const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(true)
    const [showRestricted, setShowRestricted] = useState<boolean>(false)

    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                const profile = await getUserProfile()
                setUserProfile(profile)
            } catch (err) {
                console.error('Failed to load user profile:', err)
            } finally {
                setIsLoadingProfile(false)
            }
        }
        loadUserProfile()
    }, [])

    const isAdmin = userProfile?.role === 'admin'
    const needsPermission = !isLoadingProfile && !isAdmin

    useEffect(() => {
        if (!needsPermission) return
        const timer = setTimeout(() => setShowRestricted(true), 2000)
        return () => clearTimeout(timer)
    }, [needsPermission])

    const handlePageClick = useCallback(() => {
        if (needsPermission && !showRestricted) {
            setShowRestricted(true)
        }
    }, [needsPermission, showRestricted])

    const previewUrls = useMemo(() => {
        return files.map((file) => URL.createObjectURL(file))
    }, [files])

    const handleDragOver = (e: React.DragEvent) => {
        if (readOnly) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
    }

    const handleDragLeave = (e: React.DragEvent) => {
        if (readOnly) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)
    }

    const handleDrop = (e: React.DragEvent) => {
        if (readOnly) return
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(false)

        const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
            file.type.startsWith('image/')
        )
        if (droppedFiles.length > 0) {
            setFiles((prev) => [...prev, ...droppedFiles])
            setError('')
        }
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly) return
        if (event.target.files) {
            const selectedFiles = Array.from(event.target.files).filter((file) =>
                file.type.startsWith('image/')
            )
            setFiles((prev) => [...prev, ...selectedFiles])
            setError('')
        }
    }

    const handleRemoveFile = (index: number) => {
        if (readOnly) return
        setFiles((prev) => prev.filter((_, i) => i !== index))
    }

    const handleSubmit = async () => {
        if (readOnly) return
        if (files.length === 0) {
            setError(t('training.uploadAtLeastOne'))
            return
        }
        if (!loraName.trim()) {
            setError(t('training.provideIdentityName'))
            return
        }
        setStatus('uploading')
        setError('')

        try {
            // Catch files the browser labels image/* but cannot decode (HEIC,
            // corrupt JPEGs). The trainer rejects these an hour in, so failing
            // here saves the run and the training fee.
            const undecodable = await findUndecodableImages(files)
            if (undecodable.length > 0) {
                setError(
                    t('training.undecodableImages', {
                        files: undecodable.join(', '),
                    })
                )
                setStatus('')
                return
            }

            const trimmedName = loraName.trim()
            const fileList = files.reduce((dt, file) => {
                dt.items.add(file)
                return dt
            }, new DataTransfer()).files

            setStatus('uploading')
            const trainingData = await prepareTrainingData(fileList)

            setStatus('training')
            const result = await createTrainingJob(trimmedName, trainingData, gender)

            setStatus('success')
            setLoraName('')
            setFiles([])
            setGender('female')

            // Notify parent component about success
            if (onSuccess && result?.id) {
                onSuccess(result.id, trimmedName)
            }
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'An unknown error occurred'
            setError(t('training.failedToCreateJob', { error: errorMessage }))
            setStatus('')
        }
    }

    const isProcessing = status === 'uploading' || status === 'training'
    const canSubmit = !readOnly && files.length > 0 && loraName.trim() && !isProcessing && isAdmin

    return (
        <div className={styles.container} style={{ position: 'relative' }} onClick={handlePageClick}>
            {nav.canGoBack && (
                <button className={styles.backButton} onClick={() => nav.goBack()}>
                    <ArrowLeft />
                    <span>{t('training.identities')}</span>
                </button>
            )}

            <div className={styles.content}>
                <div className={styles.titleSection}>
                    <h1 className={styles.title}>{t('training.createNewIdentity')}</h1>
                    <p className={styles.subtitle}>
                        {t('training.identityDescription')}
                    </p>
                </div>

                <div className={styles.form}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="identity-name" className={styles.label}>
                            {t('training.identityName')}
                        </label>
                        <input
                            id="identity-name"
                            type="text"
                            value={loraName}
                            onChange={(e) => setLoraName(e.target.value)}
                            placeholder={t('training.identityNamePlaceholder')}
                            className={styles.textInput}
                            disabled={readOnly || isProcessing}
                        />
                    </div>

                    <div className={styles.inputGroup}>
                        <label className={styles.label}>{t('training.gender')}</label>
                        <div className={styles.radioGroup} aria-label="Training gender">
                            <label className={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="training-gender"
                                    value="female"
                                    checked={gender === 'female'}
                                    onChange={() => setGender('female')}
                                    disabled={readOnly || isProcessing}
                                />
                                <span>{t('training.female')}</span>
                            </label>
                            <label className={styles.radioLabel}>
                                <input
                                    type="radio"
                                    name="training-gender"
                                    value="male"
                                    checked={gender === 'male'}
                                    onChange={() => setGender('male')}
                                    disabled={readOnly || isProcessing}
                                />
                                <span>{t('training.male')}</span>
                            </label>
                        </div>
                    </div>

                    <div className={styles.inputGroup}>
                        <label className={styles.label}>{t('training.referencePhotos')}</label>
                        <div
                            className={styles.dropZone}
                            data-drag-over={isDragOver}
                            data-has-files={files.length > 0}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => !readOnly && fileInputRef.current?.click()}
                            style={{ cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.6 : 1 }}
                        >
                            {files.length === 0 ? (
                                <>
                                    <ImagePlus className={styles.dropIcon} />
                                    <div className={styles.dropText}>
                                        <span className={styles.dropTextPrimary}>
                                            {t('training.dropPhotosOrClick')}
                                        </span>
                                        <span className={styles.dropTextSecondary}>
                                            {t('training.uploadInstructions')}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className={styles.previewGrid}>
                                        {files.map((file, index) => (
                                            <div key={index} className={styles.preview}>
                                                <img
                                                    src={previewUrls[index]}
                                                    alt={file.name}
                                                    className={styles.previewImage}
                                                />
                                                <button
                                                    className={styles.removeButton}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        handleRemoveFile(index)
                                                    }}
                                                    disabled={readOnly || isProcessing}
                                                >
                                                    <X />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            className={styles.addMoreButton}
                                            disabled={readOnly}
                                            onClick={() => !readOnly && fileInputRef.current?.click()}
                                        >
                                            <Upload />
                                            <span>{t('training.addMore')}</span>
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileChange}
                            className={styles.fileInput}
                            disabled={readOnly}
                        />
                    </div>

                    {error && (
                        <div className={styles.errorMessage}>
                            <span>{error}</span>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className={styles.successMessage}>
                            <Sparkles />
                            <span>{t('training.identityCreatedSuccess')}</span>
                        </div>
                    )}

                    <PrimaryButton
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        loading={isProcessing}
                        icon={<Sparkles color="white" strokeWidth={2} />}
                        loadingIcon={<Loader2 />}
                        className={styles.submitButton}
                    >
                        {isProcessing
                            ? status === 'uploading'
                                ? t('training.uploadingPhotos')
                                : t('training.creatingIdentity')
                            : t('training.createIdentity')}
                    </PrimaryButton>
                </div>
            </div>

            {showRestricted && (
                <div className={styles.restrictedOverlay}>
                    <div className={styles.restrictedMessage}>
                        <Lock />
                        <h2 className={styles.restrictedTitle}>{t('training.trainingAccessRequired')}</h2>
                        <p className={styles.restrictedText}>
                            {t('training.trainingAccessMessage')}{' '}
                            <a
                                href="mailto:support@aetherstudio.ai"
                                className={styles.restrictedEmail}
                            >
                                support@aetherstudio.ai
                            </a>
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}
